import {
  ActionIcon,
  Flex,
  Text,
  Tooltip,
  TextInput,
  Select,
  Group,
  Stack,
  Badge,
  Box
} from '@mantine/core';
import {
  IconCircleCheck,
  IconEye,
  IconLink,
  IconProgress,
  IconSearch,
  IconFilter
} from '@tabler/icons-react';
import DataTables from 'components/DataTables';
import React, { useState, useMemo } from 'react';

interface TableAssessmentRespondentsProps {
  data: any[];
  typeAssessment?: string;
  handleViewResponse: (_item: any) => void;
}

const TableAssessmentRespondents: React.FC<TableAssessmentRespondentsProps> = ({
  data,
  typeAssessment,
  handleViewResponse
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  // Detect if this table is for external respondents (different data shape)
  const isExternalList = useMemo(
    () => Array.isArray(data) && data.some((item) => 'externalEmail' in (item || {})),
    [data]
  );

  const getIsCompleted = (row: any) => {
    if (isExternalList) {
      // For Team assessments (external), modules are at the top level
      if (typeAssessment === 'Team') {
        const modules = row?.modules || [];
        return Array.isArray(modules) && modules.some((m: any) => (m?.questions || []).length > 0);
      }

      // For Individual external assessments, completion is based on participantResponses
      const prs = row?.participantResponses || [];
      return prs.some((pr: any) => {
        if (!Array.isArray(pr?.modules)) return false;
        return pr.modules.some((m: any) => (m?.questions || []).length > 0);
      });
    }
    return Array.isArray(row?.responses) && row.responses.length > 0;
  };

  // Get unique roles for filter dropdown
  const uniqueRoles = useMemo(() => {
    if (isExternalList) {
      return [{ value: 'External', label: 'External' }];
    }
    const roles = [...new Set(data.map((item) => item.respondentType).filter(Boolean))];
    return roles.map((role) => ({ value: role, label: role }));
  }, [data, isExternalList]);

  // Filter and search logic
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Apply search filter (search in respondent name/email AND participant names)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        if (isExternalList) {
          const email = (item?.externalEmail || '').toLowerCase();
          const prMatches = (item?.participantResponses || []).some((pr: any) =>
            (pr?.participantName || '').toLowerCase().includes(query)
          );
          return email.includes(query) || prMatches;
        } else {
          // Search in respondent info
          const respondentName = (item?.name || item?.binusianID || '').toLowerCase();
          const respondentEmail = (item?.email || '').toLowerCase();

          // Search in assigned participants
          const participantMatches =
            item?.participants?.some((participant: any) => {
              const participantName = (
                participant?.binusianName ||
                participant?.binusianID ||
                ''
              ).toLowerCase();
              const participantEmail = (participant?.binusianEmail || '').toLowerCase();
              return participantName.includes(query) || participantEmail.includes(query);
            }) || false;

          return (
            respondentName.includes(query) || respondentEmail.includes(query) || participantMatches
          );
        }
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((item) => {
        const isCompleted = getIsCompleted(item);
        return statusFilter === 'completed' ? isCompleted : !isCompleted;
      });
    }

    // Apply role filter
    if (roleFilter !== 'all') {
      if (isExternalList) {
        filtered = filtered.filter(() => roleFilter === 'External');
      } else {
        filtered = filtered.filter((item) => item.respondentType === roleFilter);
      }
    }

    return filtered;
  }, [data, searchQuery, statusFilter, roleFilter]);

  const columns = useMemo(() => {
    if (isExternalList) {
      return [
        {
          heading: 'Email',
          props: (row: any) => (
            <Text size='sm' fw={600}>
              {row?.externalEmail || '-'}
            </Text>
          ),
          sortable: true,
          active: true,
          disabled: false,
          id: 'externalEmail'
        },
        {
          heading: 'Role',
          props: () => 'External',
          sortable: false,
          active: true,
          disabled: false,
          id: 'role'
        },
        {
          heading: 'Assigned Participants',
          props: (row: any) => {
            // For Team assessments, show a Team badge
            if (typeAssessment === 'Team') {
              return (
                <Stack gap={2}>
                  <Badge size='sm' variant='light' color='blue' style={{ maxWidth: '200px' }}>
                    <Text size='xs' truncate>
                      Team
                    </Text>
                  </Badge>
                </Stack>
              );
            }

            const prs = row?.participantResponses || [];
            if (prs.length === 0) {
              return (
                <Text size='sm' c='dimmed' fs='italic'>
                  No participants
                </Text>
              );
            }
            return (
              <Stack gap={2}>
                {prs.slice(0, 2).map((pr: any, index: number) => (
                  <Badge
                    key={index}
                    size='sm'
                    variant='light'
                    color='blue'
                    style={{ maxWidth: '200px' }}
                  >
                    <Text size='xs' truncate>
                      {pr?.participantName || 'Participant'}
                    </Text>
                  </Badge>
                ))}
                {prs.length > 2 && (
                  <Text size='xs' c='dimmed'>
                    +{prs.length - 2} more
                  </Text>
                )}
              </Stack>
            );
          },
          active: true,
          disabled: false
        },
        {
          heading: 'Response Status',
          props: (row: any) => {
            const isCompleted = getIsCompleted(row);
            return (
              <Tooltip label={isCompleted ? 'Completed' : 'In Progress'}>
                <Text component='span' c={isCompleted ? 'green.5' : 'yellow'}>
                  {isCompleted ? <IconCircleCheck /> : <IconProgress />}
                </Text>
              </Tooltip>
            );
          },
          active: true,
          disabled: false
        },
        {
          heading: 'Actions',
          props: (row: any) => {
            const isCompleted = getIsCompleted(row);
            return (
              <Flex gap={10} align='center'>
                {isCompleted ? (
                  <Tooltip label='View Responses'>
                    <ActionIcon
                      variant='transparent'
                      c={'grey.5'}
                      onClick={() => handleViewResponse({ ...row, isExternal: true })}
                    >
                      <IconEye />
                    </ActionIcon>
                  </Tooltip>
                ) : (
                  <Tooltip label='No responses yet'>
                    <Text c={'grey.2'}>
                      <IconEye />
                    </Text>
                  </Tooltip>
                )}
              </Flex>
            );
          },
          active: true,
          disabled: false
        }
      ];
    }

    // Default (internal respondents)
    return [
      {
        heading: 'Respondent Name',
        props: (row: { name?: string; binusianID?: string }) => (
          <Text size='sm' fw={600}>
            {row?.name || row?.binusianID || '-'}
          </Text>
        ),
        sortable: true,
        active: true,
        disabled: false,
        id: 'name'
      },
      {
        heading: 'Email',
        props: 'email',
        sortable: true,
        active: true,
        disabled: false,
        id: 'email'
      },
      {
        heading: 'Role',
        props: 'respondentType',
        sortable: true,
        active: true,
        disabled: false,
        id: 'respondentType'
      },
      {
        heading: 'Assigned Participants',
        props: (row: { participants?: any[] }) => {
          const participants = row?.participants || [];

          if (participants.length === 0) {
            return (
              <Text size='sm' c='dimmed' fs='italic'>
                No participants assigned
              </Text>
            );
          }

          return (
            <Box>
              {typeAssessment === 'Team' ? (
                <Stack gap={2}>
                  <Badge size='sm' variant='light' color='blue' style={{ maxWidth: '200px' }}>
                    <Text size='xs' truncate>
                      Team
                    </Text>
                  </Badge>
                </Stack>
              ) : (
                <Stack gap={2}>
                  {participants.slice(0, 2).map((participant, index) => (
                    <Badge
                      key={index}
                      size='sm'
                      variant='light'
                      color='blue'
                      style={{ maxWidth: '200px' }}
                    >
                      <Text size='xs' truncate>
                        {participant?.binusianName || participant?.binusianID || 'Unknown'}
                      </Text>
                    </Badge>
                  ))}
                  {participants.length > 2 && (
                    <Text size='xs' c='dimmed'>
                      +{participants.length - 2} more
                    </Text>
                  )}
                </Stack>
              )}
            </Box>
          );
        },
        active: true,
        disabled: false
      },
      {
        heading: 'Response Status',
        props: (row: any) => {
          const isCompleted = getIsCompleted(row);

          return (
            <Tooltip label={isCompleted ? 'Completed' : 'In Progress'}>
              <Text component='span' c={isCompleted ? 'green.5' : 'yellow'}>
                {isCompleted ? <IconCircleCheck /> : <IconProgress />}
              </Text>
            </Tooltip>
          );
        },
        active: true,
        disabled: false
      },
      {
        heading: 'Actions',
        props: (row: any) => {
          const isCompleted = getIsCompleted(row);

          return (
            <Flex gap={10} align='center'>
              <Tooltip label='Link to Assessment'>
                <ActionIcon
                  variant='transparent'
                  c={'grey.5'}
                  onClick={() => window.open(`${row.link}`, '_blank')}
                >
                  <IconLink />
                </ActionIcon>
              </Tooltip>
              {isCompleted ? (
                <Tooltip label='View Responses'>
                  <ActionIcon
                    variant='transparent'
                    c={'grey.5'}
                    onClick={() => handleViewResponse(row)}
                  >
                    <IconEye />
                  </ActionIcon>
                </Tooltip>
              ) : (
                <Tooltip label='No responses yet'>
                  <Text c={'grey.2'}>
                    <IconEye />
                  </Text>
                </Tooltip>
              )}
            </Flex>
          );
        },
        active: true,
        disabled: false
      }
    ];
  }, [isExternalList, typeAssessment, handleViewResponse]);

  return (
    <Stack gap='md'>
      {/* Search and Filter Controls */}
      <Group justify='space-between' gap='sm' wrap='wrap' align='flex-end'>
        <TextInput
          placeholder='Search respondent or participant name/email...'
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          style={{ flex: '1 1 320px', minWidth: 220 }}
        />
        <Group gap='sm' wrap='wrap' align='flex-end' justify='flex-end'>
          <Select
            placeholder='Filter by role'
            value={roleFilter}
            onChange={(value) => setRoleFilter(value || 'all')}
            data={[{ value: 'all', label: 'All Roles' }, ...uniqueRoles]}
            leftSection={<IconFilter size={16} />}
            style={{ flex: '1 1 160px', minWidth: 140, maxWidth: 200 }}
          />
          <Select
            placeholder='Filter by status'
            value={statusFilter}
            onChange={(value) => setStatusFilter(value || 'all')}
            data={[
              { value: 'all', label: 'All Status' },
              { value: 'completed', label: 'Completed' },
              { value: 'in-progress', label: 'In Progress' }
            ]}
            leftSection={<IconFilter size={16} />}
            style={{ flex: '1 1 180px', minWidth: 150, maxWidth: 220 }}
          />
        </Group>
      </Group>

      {/* Results summary */}
      <Text size='sm' c='dimmed'>
        Showing {filteredData.length} of {data.length} respondents
      </Text>

      <DataTables columns={columns as any} data={filteredData} withBulkData={true} />
    </Stack>
  );
};

export default TableAssessmentRespondents;
